import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomModal from '../Modal/CustomModal';
import { customSwal } from '../CustomSwal';
import { ITEM_TYPES, ITEM_TYPE_NAMES } from './itemTypes';

const DEFAULT_TYPE = 'content';

const CreateItemModal = ({ isOpen, onClose, activeConsole, dispatch, createConsoleItem }) => {
  const [itemName, setItemName] = useState('');
  const [itemType, setItemType] = useState(DEFAULT_TYPE);
  const [draft, setDraft] = useState(() => ITEM_TYPES[DEFAULT_TYPE].emptyDraft());
  // Editors read their value once on mount, so only render them after the form has been reset
  const [ready, setReady] = useState(false);

  // The modal stays mounted between uses, so start from a blank form each time it opens
  useEffect(() => {
    if (isOpen) {
      setItemName('');
      setItemType(DEFAULT_TYPE);
      setDraft(ITEM_TYPES[DEFAULT_TYPE].emptyDraft());
      setReady(true);
    } else {
      setReady(false);
    }
  }, [isOpen]);

  const updateDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  };

  // Keep what was typed for other types, so switching back and forth doesn't lose work
  const handleTypeChange = (name) => {
    setItemType(name);
    setDraft((prev) => ({ ...ITEM_TYPES[name].emptyDraft(), ...prev }));
  };

  const handleSubmit = () => {
    if (!itemName.trim()) {
      customSwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please enter a display name',
      });
      return;
    }

    const type = ITEM_TYPES[itemType];
    const error = type.validate(draft, { isNew: true });
    if (error) {
      customSwal.fire({
        icon: 'error',
        title: 'Error',
        text: error,
      });
      return;
    }

    dispatch(createConsoleItem({
      displayName: itemName,
      itemType,
      consoleId: activeConsole,
      ...type.toPayload(draft),
    }));
  };

  const { Editor } = ITEM_TYPES[itemType];

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Console Item"
      onConfirm={handleSubmit}
      confirmText="Create"
      size="2xl"
    >
      <div className="space-y-4 text-left">
        <div>
          <label className="block mb-1 font-medium">Display Name:</label>
          <p className="mb-2 text-xs text-gray-400">Your website looks the item up by this exact name.</p>
          <input
            type="text"
            className="w-full p-2 rounded bg-gray-700 text-white"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-2 font-medium">Item Type:</label>
          <div className="grid gap-2 sm:grid-cols-3">
            {ITEM_TYPE_NAMES.map((name) => {
              const type = ITEM_TYPES[name];
              const active = name === itemType;

              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleTypeChange(name)}
                  aria-pressed={active}
                  className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                    active ? 'border-blue-500 bg-blue-500/10' : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <FontAwesomeIcon icon={type.icon} className={`mt-1 ${active ? 'text-blue-300' : 'text-gray-400'}`} />
                  <span>
                    <span className="block font-semibold">{type.label}</span>
                    <span className="block text-xs text-gray-400">{type.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-gray-400">The type can't be changed after the item is created.</p>
        </div>

        {ready && <Editor draft={draft} onChange={updateDraft} consoleId={activeConsole} />}
      </div>
    </CustomModal>
  );
};

export default CreateItemModal;
